import { OrganizationContext } from '../../../../components/contexts/organization';
import { CouponPackMastersQuery, useCouponPackMastersLazyQuery } from '../../../../graphql/generated';
import { useCallback, useContext, useEffect, useState } from 'react';
import { SearchFormValues } from '../CouponPackMastersPage';
import { useSnackbar } from 'notistack';

const PageItemsCount = 25;

export type ListCouponPackMasters = {
  loading: boolean;
  onSearchCouponPackMasters: (condition: SearchFormValues) => void;
  data?: CouponPackMastersQuery;
  onFetchMore: () => void;
};

export const useListCouponPackMasters = (): ListCouponPackMasters => {
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext.organization;
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [listQuery, queryContext] = useCouponPackMastersLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: () => {
      setLoading(false);
    },
    onError: () => {
      enqueueSnackbar('サーバーエラーが発生した', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    },
  });

  // 初回、検索条件なしで表示する
  useEffect(() => {
    if (organization) {
      setLoading(true);
      listQuery({
        variables: {
          organizationId: organization.id,
          limit: PageItemsCount,
        },
      });
    }
  }, [listQuery, organization]);

  // 動的な検索条件をセットしてクーポンマスタを検索する
  const onSearchCouponPackMasters = useCallback(
    (data: SearchFormValues) => {
      if (organization) {
        setLoading(true);
        console.log('Searching with condition:', data);
        listQuery({
          variables: {
            organizationId: organization.id,
            [data.searchCondition]: data.value,
            limit: PageItemsCount,
          },
        });
      }
    },
    [listQuery, organization, orgContext]
  );

  // さらに読み込む
  const onFetchMore = useCallback(async () => {
    if (organization && queryContext?.fetchMore && queryContext.variables) {
      setLoading(true);
      await queryContext.fetchMore({
        variables: {
          organizationId: queryContext.variables.organizationId,
          couponName: queryContext.variables.couponName,
          couponCode: queryContext.variables.couponCode,
          nextToken: queryContext.data?.couponPackMasters.pageInfo.nextToken,
          limit: PageItemsCount,
        },
        updateQuery: (prevQuery, { fetchMoreResult }) => {
          setLoading(false);
          if (!fetchMoreResult) {
            return prevQuery;
          }
          return {
            couponPackMasters: {
              __typename: fetchMoreResult.couponPackMasters.__typename,
              nodes: [...prevQuery.couponPackMasters.nodes, ...fetchMoreResult.couponPackMasters.nodes],
              pageInfo: fetchMoreResult.couponPackMasters.pageInfo,
            },
          };
        },
      });
    }
  }, [queryContext, organization]);

  return {
    loading,
    onSearchCouponPackMasters,
    data: queryContext.data,
    onFetchMore,
  };
};
